import { Injectable, signal } from '@angular/core';

/** Opciones para el diálogo de confirmación de acciones destructivas. */
export interface ConfirmOptions {
  /** Título del diálogo. */
  title?: string;
  /** Mensaje principal (qué se va a hacer / sobre qué). */
  message: string;
  /** Texto del botón que confirma. */
  confirmText?: string;
  /** Texto del botón que cancela. */
  cancelText?: string;
  /** Tono del botón de confirmación (rojo para destructivo). */
  tone?: 'danger' | 'primary';
}

type ConfirmState = Required<ConfirmOptions>;

/**
 * Servicio de confirmación global.
 *
 * Cualquier acción destructiva (borrar, desactivar) la envuelve con
 * `await confirm.ask({...})`. La UI la pinta <app-confirm-dialog> (montado una
 * sola vez en el root), igual que el toast-host.
 *
 *   if (await this.confirm.ask({ message: '¿Eliminar el equipo X?' })) {
 *     this.repo.remove(id).subscribe(...);
 *   }
 */
@Injectable({ providedIn: 'root' })
export class ConfirmationService {
  private readonly _state = signal<ConfirmState | null>(null);
  readonly state = this._state.asReadonly();

  private resolver: ((value: boolean) => void) | null = null;

  /** Abre el diálogo y resuelve `true` solo si el usuario confirma. */
  ask(options: ConfirmOptions): Promise<boolean> {
    // Si ya había uno abierto, se considera cancelado.
    this.settle(false);
    this._state.set({
      title: 'Confirmar acción',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      tone: 'danger',
      ...options,
    });
    return new Promise<boolean>((resolve) => {
      this.resolver = resolve;
    });
  }

  confirm(): void {
    this.settle(true);
  }

  cancel(): void {
    this.settle(false);
  }

  private settle(value: boolean): void {
    const resolver = this.resolver;
    this.resolver = null;
    this._state.set(null);
    resolver?.(value);
  }
}
